import { Box, Button, MenuItem, Select, SxProps, TextField, Typography, useTheme } from "@mui/material";

import { TimePicker } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { MuiTelInput } from "mui-tel-input";

import { useEffect, useRef, useState } from "react";
import { ProductInput, ProductOrService } from "../../data/products-and-services";
import { GeneralButtonLabel, ProductFormText } from "../../data/texts";
import { Input, InputMap, InputType, SelectOption } from "../../models/common.models";
import { FileWithId } from "../../models/uploaded-file";
import { EventLoppService } from "../../services/event-loop.service";
import { UtilsService } from "../../services/utils.service";
import StyledTextArea from "../../styled-cmps/text-area";
import FileUploaderList from "../common/FileUploaderList";
import useScreen from "../common/UseScreen";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/system.store";
import { DateTimeService } from "../../services/date-time.service";

interface ProductFormProps {
  product: ProductOrService;
  containerSx?: SxProps;
  onSubmit?: (formData: FormData, href: string) => void;
}

export type FormData = {
  [key: string]: any;
};

enum StaticOptionId {
  MAP_SELECT = "mapSelect",
}

const ProductForm = ({ product, containerSx, onSubmit }: ProductFormProps) => {
  const { isMobile } = useScreen();
  const [mapSelect, setMapSelect] = useState<Input | null>();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedMap, setSelectedMap] = useState<string>("");
  const theme = useTheme();
  const inputsContainerRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<FormData>();
  const dispatch = useDispatch();
  const [selectsValues, setSelectsValues] = useState<{
    [key: string]: string;
  }>();

  const scrollFormToTop = () => {
    if (inputsContainerRef.current) {
      inputsContainerRef.current.scrollTo({ top: 0 });
    }
  };

  const getInputList = () => {
    if (isInputMap(product.inputs)) {
      return product.inputs.inputMap[selectedMap]?.inputs || [];
    }
    return product.inputs.inputs || [];
  };

  const initFormData = () => {
    const initialData: FormData = {};
    const selects: { [key: string]: string } = {};
    if (!isInputMap(product.inputs)) {
      setMapSelect(null);
    }

    getInputList().forEach((input) => {
      if (input.type === InputType.SELECT && input.options) {
        const firstOption = input.options[0].id;
        selects[input.name] = firstOption;

        initialData[input.name] = firstOption;
        return;
      }
      if (input.type === InputType.FILE) {
        initialData[input.name] = [];
        return;
      }

      initialData[input.name] = "";
    });

    if ((!formData || !!selectedMap) && !UtilsService.isObjectEmpty(initialData)) {
      if (selectedMap) {
        setFormData({ ...initialData, type: selectedMap });
      } else {
        setFormData({ ...initialData });
      }
    }
    if (!UtilsService.isObjectEmpty(selects)) {
      setSelectsValues(selects);
    }
  };

  const handlePhoneInput = (phoneNumber: string, inputName: string) => {
    setFormData({
      ...formData,
      [inputName]: phoneNumber.replaceAll(" ", ""),
    });
  };

  const handleDateChange = (value: Date | null, dateInputName: string) => {
    if (value) {
      setFormData({ ...formData, [dateInputName]: value.valueOf() });
    }
  };

  const handleTimeChange = (ev: Date | null, timeInputName: string) => {
    if (ev) {
      setFormData({ ...formData, [timeInputName]: dayjs(ev).format("HH:mm") });
    }
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, isNumber?: boolean) => {
    dispatch(showSnackbar({ msg: "" }));
    const { name } = event.target;
    let { value } = event.target;

    if (isNumber && value) {
      const input = event.target as HTMLInputElement;
      if (input.step === "1") {
        value = value.replace(/[^\d]/g, "");
        event.target.value = value;
      }
    }

    validateField(name, value);

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: isNumber && value ? Number(value) : value,
    }));
  };

  const handleFilesUpload = async (files: FileWithId[], inputName: string) => {
    if (files.length) {
      const file = files[0];
      setFormData({ ...formData, [inputName]: file.content });
    }
  };

  const handleFileRemove = (index: number, inputName: string) => {
    const filteredFileList = formData?.[inputName] as string[];
    filteredFileList.splice(index, 1);
    setFormData({ ...formData, [inputName]: filteredFileList });
  };

  const handleSelect = (option: SelectOption, selectName: string) => {
    if (selectName === StaticOptionId.MAP_SELECT) {
      setSelectedMap(option.id);
    }

    setSelectsValues({ ...selectsValues, [selectName]: option.id });
    setFormData({ ...formData, [selectName]: option.label });
  };

  const validateField = (name: string, value: any) => {
    if (!value) {
      setErrors((prev) => ({ ...prev, [name]: "This field is required" }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData) return;
    Object.keys(formData).forEach((key) => {
      const value = formData[key];

      if (typeof value === "number" && value < 0) {
        if (key === "date" && !DateTimeService.isValidDate(value)) {
          newErrors[key] = "Date is not valid";
        } else {
          newErrors[key] = "Numbers must be bigger than 0";
        }
      }

      if (!value) {
        newErrors[key] = "This field is required";
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (onSubmit) {
      let href = product.href;
      if (selectedMap && isInputMap(product.inputs)) {
        href += product.inputs.inputMap[selectedMap].href;
      }
      onSubmit(formData, href);
    }
  };

  const isInputMap = (inputs: ProductInput | InputMap): inputs is InputMap => {
    return "inputMap" in inputs;
  };

  const buildMapSelectOptions = (): SelectOption[] => {
    if (isInputMap(product.inputs)) {
      return Object.keys(product.inputs.inputMap).map((key) => {
        return { label: key.camelCaseToSpaces(), id: key };
      });
    }
    return [];
  };

  const getMapSelect = () => {
    if (isInputMap(product.inputs)) {
      const selectOptions = buildMapSelectOptions();
      if (selectOptions) {
        setMapSelect({
          options: selectOptions,
          type: InputType.SELECT,
          label: product.inputs.mapSelectLabel,
          name: StaticOptionId.MAP_SELECT,
        });
      }
    }
  };

  const getInputTypeList = (inputType: InputType) => {
    return getInputList().filter((input) => input.type === inputType);
  };

  const getErrorStyle = (inputName: string): SxProps => {
    return {
      "& .MuiOutlinedInput-root": {
        borderColor: errors[inputName] ? "red" : undefined,
      },
      "& .MuiInputBase-input": {
        fontWeight: 300,
        color: theme.palette.primary.main,
      },
      "& .MuiInputBase-input::placeholder": {
        color: theme.palette.primary.main,
        fontWeight: 400,
        opacity: 0.7,
      },
    };
  };

  useEffect(() => {
    getMapSelect();
    initFormData();
  }, []);

  useEffect(() => {
    if (product) {
      setErrors({});
      if (isInputMap(product.inputs)) {
        const firstMap = Object.keys(product.inputs.inputMap)[0];
        setSelectedMap(firstMap);
      }
      EventLoppService.nextTick(() => {
        scrollFormToTop();
        getMapSelect();
        initFormData();
      }, 20);
    }
  }, [product]);

  useEffect(() => {
    setErrors({});
    initFormData();
  }, [selectedMap]);

  return (
    <Box
      sx={{
        maxHeight: isMobile ? "100%" : `calc(100dvh - ${theme.custom.appHeaderHeight} - ${theme.custom.appFooterHeight} - 48px)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: isMobile ? "none" : "1px #4C4E641F solid",
        borderRadius: "10px",
        padding: isMobile ? "0" : "40px",
        ...containerSx,
      }}
    >
      <Typography
        sx={{
          fontWeight: "300",
          fontSize: "24px",
          color: theme.palette.primary.main,
          marginBottom: "5px",
        }}
      >
        {product.title} {ProductFormText.PURCHASE_FORM}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: theme.palette.primary.main,
          marginBottom: "28px",
        }}
      >
        {ProductFormText.FILL_DETAILS}
      </Typography>
      <Box
        ref={inputsContainerRef}
        sx={{
          marginBottom: "15px",
          padding: isMobile ? "0" : "10px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          alignSelf: "stretch",
          gap: "16px",
        }}
      >
        {/* MAP SELECT TYPE */}
        {mapSelect && (
          <Box key={mapSelect.name}>
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontWeight: "400",
                marginBottom: "5px",
              }}
            >
              {mapSelect.label}
            </Typography>
            <Select
              required
              key={mapSelect.name}
              value={selectedMap || ""}
              sx={{ width: "100%" }}
              inputProps={{
                sx: {
                  fontWeight: "300",
                  color: theme.palette.primary.main,
                },
              }}
            >
              {mapSelect.options?.map((option) => (
                <MenuItem
                  key={option.id}
                  sx={{
                    fontWeight: "300",
                    color: theme.palette.primary.main,
                  }}
                  onClick={() => handleSelect(option, mapSelect.name)}
                  value={option.id}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
        {/* SELECT TYPE */}
        {getInputTypeList(InputType.SELECT).map((select) => (
          <Box key={select.name}>
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontWeight: "400",
                marginBottom: "5px",
              }}
            >
              {select.label}
            </Typography>
            <Select
              required
              key={select.name}
              value={selectsValues?.[select.name] || ""}
              sx={{ width: "100%" }}
              inputProps={{
                sx: {
                  fontWeight: "300",
                  color: theme.palette.primary.main,
                },
              }}
            >
              {select.options?.map((option) => (
                <MenuItem
                  key={option.id}
                  sx={{
                    fontWeight: "300",
                    color: theme.palette.primary.main,
                  }}
                  onClick={() => handleSelect(option, select.name)}
                  value={option.id}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        ))}
        {/* TEXT TYPE */}
        {getInputTypeList(InputType.TEXT).map((input) => (
          <TextField
            onBlur={() => validateField(input.name, formData?.[input.name])}
            required={!input.optional}
            type={input.type}
            key={input.name}
            name={input.name}
            sx={{
              input: {
                fontWeight: "300",
                color: theme.palette.primary.main,
              },
              "& .MuiInputBase-input::placeholder": {
                fontWeight: "300",
                color: theme.palette.primary.main,
              },
            }}
            error={!!errors[input.name]}
            placeholder={input.placeholder}
            label={input.label}
            value={formData?.[input.name] || ""}
            onChange={(ev) => handleInput(ev)}
          />
        ))}

        {/* PHONE */}
        {getInputTypeList(InputType.PHONE).map((phoneInput) => (
          <MuiTelInput
            key={phoneInput.name}
            label={phoneInput.label}
            sx={{
              "& input": {
                fontWeight: "400",
              },
            }}
            required={!phoneInput.optional}
            name={phoneInput.name}
            defaultCountry="IL"
            value={`${formData?.[phoneInput.name]}`}
            onChange={(value) => handlePhoneInput(value, phoneInput.name)}
          />
        ))}

        {/* TIME */}
        {getInputTypeList(InputType.TIME).map((timeInput) => (
          <TimePicker
            label={timeInput.label}
            key={timeInput.name}
            name={timeInput.name}
            onChange={(ev) => handleTimeChange(ev, timeInput.name)}
            slotProps={{
              textField: {
                error: !!errors[timeInput.name],
              },
            }}
            sx={getErrorStyle(timeInput.name)}
          />
        ))}
        {/* DATE */}
        {getInputTypeList(InputType.DATE).map((dateInput) => (
          <DatePicker
            label={dateInput.label}
            key={dateInput.name}
            onChange={(dayJs: Date | null) => {
              handleDateChange(dayJs, dateInput.name);
              validateField(dateInput.name, dayJs);
            }}
            slotProps={{
              textField: {
                error: !!errors[dateInput.name],
              },
            }}
            sx={getErrorStyle(dateInput.name)}
          />
        ))}

        {/* NUMBER TYPE */}
        {getInputTypeList(InputType.NUMBER).map((numberInput) => (
          <TextField
            required={!numberInput.optional}
            sx={{
              "& .MuiInputBase-input": {
                fontWeight: 300,
                color: theme.palette.primary.main,
              },
              "& .MuiInputBase-input::placeholder": {
                color: theme.palette.primary.main,
                fontWeight: 400,
                opacity: 0.7,
              },
            }}
            error={!!errors[numberInput.name]}
            key={numberInput.name}
            name={numberInput.name}
            type="number"
            placeholder={numberInput.placeholder}
            label={numberInput.label}
            value={formData?.[numberInput.name] || ""}
            onChange={(ev) => handleInput(ev, true)}
            inputProps={{
              step: numberInput.step,
              min: numberInput.min,
              max: numberInput.max,
            }}
            onKeyDown={(e) => {
              if (numberInput.step === 1 && (e.key === "." || e.key === ",")) {
                e.preventDefault();
              }
            }}
          ></TextField>
        ))}
        {/* TEXT AREA TYPE */}
        {getInputTypeList(InputType.TEXTAREA).map((textarea) => (
          <StyledTextArea
            onChange={handleInput}
            key={textarea.name}
            name={textarea.name}
            value={formData?.[textarea.name]}
            error={!!errors[textarea.name]}
            multiline
            rows={1}
            placeholder={textarea.placeholder}
          />
        ))}

        {/* FILE UPLOAD */}
        {getInputTypeList(InputType.FILE).map((fileInput) => (
          <Box key={fileInput.name}>
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontWeight: "400",
                marginBottom: "10px",
              }}
            >
              {fileInput.label}
            </Typography>
            <FileUploaderList
              hideSubmitButton
              onFileRemove={(index: number) => handleFileRemove(index, fileInput.name)}
              singleFile
              onFileUploaded={(files: FileWithId[]) => handleFilesUpload(files, fileInput.name)}
              fileUploaderSx={{ height: "173px" }}
            />
          </Box>
        ))}
      </Box>
      <Button onClick={handleSubmit} variant="contained" sx={{ background: theme.palette.text.primary, width: "100%" }}>
        {GeneralButtonLabel.SUBMIT}
      </Button>
    </Box>
  );
};

export default ProductForm;
